import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Search, Banknote, Clock, CheckCircle2, AlertTriangle, FileText, Archive, ArrowUpRight } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const STATUS_META = {
  submitted:     { label: "Submitted",      tone: "bg-[#0A192F]/5 text-[#4A5568] border-[#0A192F]/15", icon: Clock },
  in_collection: { label: "In collection",  tone: "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/30", icon: Banknote },
  recovered:     { label: "Recovered",      tone: "bg-[#22C55E]/10 text-[#15803D] border-[#22C55E]/30", icon: CheckCircle2 },
  uncollectible: { label: "Uncollectible",  tone: "bg-[#EF4444]/10 text-[#B91C1C] border-[#EF4444]/30", icon: AlertTriangle },
  closed:        { label: "Closed",         tone: "bg-[#4A5568]/10 text-[#4A5568] border-[#4A5568]/30", icon: Archive },
};

export default function CollectionsPage() {
  const [claims, setClaims] = useState([]);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    apiClient.get(`/claims${showArchived ? "?archived=true" : ""}`)
      .then(({ data }) => setClaims(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, [showArchived]);

  const filtered = claims.filter((c) =>
    !search ||
    c.debtor?.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.subscriber?.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totals = claims.reduce((acc, c) => {
    acc.owed += Number(c.total_amt_owed || 0);
    acc.recovered += Number(c.total_recovered || 0);
    acc.fee += Number(c.fee_due_usd || 0);
    if (c.status === "recovered") acc.recovered_count += 1;
    return acc;
  }, { owed: 0, recovered: 0, fee: 0, recovered_count: 0 });

  return (
    <AppShell>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-overline text-[#4A5568] mb-2">Flat Fee Collections</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#0A192F]" data-testid="collections-title">
            Recover what you're owed<span className="text-[#F59E0B]">.</span>
          </h1>
          <p className="text-[#4A5568] mt-2 max-w-2xl">
            $85 flat fee per claim. You keep 100% of recoveries. Demand letters, calls, and credit-bureau
            tradelines — all handled by our collection partner.
          </p>
        </div>
        <Link to="/collections/new" data-testid="collections-new-btn">
          <Button className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-5">
            <Plus className="h-4 w-4 mr-2" /> New claim
          </Button>
        </Link>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Claims" value={claims.length} />
        <SummaryCard label="Total owed" value={`$${totals.owed.toFixed(0)}`} />
        <SummaryCard label="Recovered" value={`$${totals.recovered.toFixed(0)}`} tone="text-[#15803D]" />
        <SummaryCard label="Fees billed" value={`$${totals.fee.toFixed(0)}`} sub={`${claims.length} × $85`} />
      </div>

      {/* List */}
      <div className="bg-white border border-[#0A192F]/10 rounded-sm">
        <div className="px-5 py-3 border-b border-[#0A192F]/10 flex items-center gap-3 flex-wrap">
          <Search className="h-4 w-4 text-[#4A5568] shrink-0" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by debtor name or company…"
            className="border-0 shadow-none focus-visible:ring-0 h-9 max-w-md"
            data-testid="collections-search" />
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-[#4A5568] font-mono">{filtered.length} claim{filtered.length !== 1 ? "s" : ""}</span>
            <button type="button" onClick={() => setShowArchived((v) => !v)}
              data-testid="collections-toggle-archived"
              className={`h-9 px-3 rounded-sm text-xs font-mono tracking-[0.12em] uppercase border transition-colors ${
                showArchived
                  ? "bg-[#0A192F] text-white border-[#0A192F]"
                  : "bg-white text-[#4A5568] border-[#0A192F]/15 hover:border-[#0A192F]/30"
              }`}>
              {showArchived ? "Showing archived" : "Show archived"}
            </button>
          </div>
        </div>
        {error && <div className="p-4 text-[#EF4444] text-sm">{error}</div>}
        {filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="collections-empty">
            <FileText className="h-8 w-8 text-[#4A5568] mx-auto mb-3" />
            <div className="font-serif text-xl text-[#0A192F]">No claims yet</div>
            <p className="text-sm text-[#4A5568] mt-2 max-w-md mx-auto">Submit your first claim with the debtor's last known contact info and a short reason. We'll take it from there.</p>
            <Link to="/collections/new" className="mt-5 inline-block">
              <Button className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-10 px-4">
                <Plus className="h-4 w-4 mr-2" /> Submit a claim
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#0A192F]/8">
            {filtered.map((c) => <ClaimRow key={c.id} c={c} onOpen={() => nav(`/collections/${c.id}`)} />)}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

function SummaryCard({ label, value, sub, tone = "text-[#0A192F]" }) {
  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-4">
      <div className="font-overline text-[#4A5568]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#4A5568] font-mono mt-0.5">{sub}</div>}
    </div>
  );
}

function ClaimRow({ c, onOpen }) {
  const meta = STATUS_META[c.status] || STATUS_META.submitted;
  const pct = c.total_amt_owed ? Math.min(100, (Number(c.total_recovered || 0) / c.total_amt_owed) * 100) : 0;
  return (
    <li className="px-5 py-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center cursor-pointer hover:bg-[#F3EFEA]/40"
        onClick={onOpen} data-testid={`claim-row-${c.id}`}>
      <div className="sm:col-span-4 min-w-0">
        <div className="font-serif text-base text-[#0A192F] truncate">{c.debtor?.name || "Debtor"}</div>
        <div className="text-[11px] text-[#4A5568] font-mono truncate">
          {c.id.slice(0, 8).toUpperCase()} · {fmtDate(c.created_at)}
        </div>
      </div>
      <div className="sm:col-span-2 text-xs text-[#4A5568] truncate">
        {c.subscriber?.company_name}
      </div>
      <div className="sm:col-span-3">
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-lg text-[#0A192F]">${Number(c.total_amt_owed).toFixed(2)}</span>
          <span className="text-[11px] text-[#4A5568]">owed</span>
        </div>
        <div className="mt-1 h-1 bg-[#0A192F]/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#22C55E]" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] text-[#15803D] mt-1">${Number(c.total_recovered || 0).toFixed(2)} recovered</div>
      </div>
      <div className="sm:col-span-2">
        <Badge variant="outline" className={`rounded-sm text-[10px] uppercase font-mono ${meta.tone}`}>
          <meta.icon className="h-3 w-3 mr-1" /> {meta.label}
        </Badge>
      </div>
      <div className="sm:col-span-1 flex justify-end">
        <ArrowUpRight className="h-4 w-4 text-[#4A5568]" />
      </div>
    </li>
  );
}
